import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import InlineNotice from './InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import UnifiedPaymentModal from './UnifiedPaymentModal';
import { formatCurrency as formatCurrencyBySystem, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';

const PurchaseModal = ({ isOpen, onClose, onPurchaseSaved, purchase, isEditable = false }) => {
  const [vendor, setVendor] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [gst, setGst] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState('');
  const [subTotal, setSubTotal] = useState('');
  const [tax, setTax] = useState('');
  const [total, setTotal] = useState('');
  const [vendors, setVendors] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingVendors, setFetchingVendors] = useState(false);
  const [fetchingPayments, setFetchingPayments] = useState(false);
  const [error, setError] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState(purchase?.status || 'Draft');
  const [confirming, setConfirming] = useState(false);
  const [userRole, setUserRole] = useState('user');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const { showInlineNotice } = useInlineNotice();

  const isEditMode = !!purchase;
  const isReadOnly = isEditMode && !isEditable;
  const canViewHistory = isEditMode && userRole !== 'user';

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (!user) return;
    try {
      const userData = JSON.parse(user);
      setUserRole(userData.role || 'user');
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
  }, []);

  // Fetch vendors when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchVendors();
      if (purchase) {
        fetchPaymentHistory();
      }
    }
  }, [isOpen, purchase]);

  const fetchPaymentHistory = async () => {
    if (!purchase) return;
    
    setFetchingPayments(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/payments/purchase/${purchase._id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setPayments(response.data.payments || []);
    } catch (err) {
      console.error('Error fetching payment history:', err);
    } finally {
      setFetchingPayments(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return 'N/A';
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return 'N/A';
    return dateValue.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const buildHistoryEntries = () => {
    if (!purchase) return [];
    const entries = [];
    const createdAt = purchase.createdAt;
    const updatedAt = purchase.updatedAt;
    const createdByLabel = purchase.createdBy?.name || 'Unknown';

    if (createdAt) {
      entries.push({
        action: 'Created',
        dateTime: createdAt,
        author: createdByLabel,
        sortTime: new Date(createdAt).getTime()
      });
    }

    if (purchaseStatus && purchaseStatus !== 'Draft' && (updatedAt || createdAt)) {
      const statusTime = updatedAt || createdAt;
      entries.push({
        action: `Status: ${purchaseStatus}`,
        dateTime: statusTime,
        author: createdByLabel,
        sortTime: statusTime ? new Date(statusTime).getTime() : 0
      });
    } else if (updatedAt && createdAt && updatedAt !== createdAt) {
      entries.push({
        action: 'Updated',
        dateTime: updatedAt,
        author: createdByLabel,
        sortTime: new Date(updatedAt).getTime()
      });
    }

    if (payments.length) {
      payments.forEach((payment) => {
        const paymentTime = payment.paymentDate || payment.createdAt;
        entries.push({
          action: 'Payment Added',
          dateTime: paymentTime,
          author: payment.createdBy?.name || 'Unknown',
          sortTime: paymentTime ? new Date(paymentTime).getTime() : 0
        });
      });
    }

    return entries.sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0));
  };

  const fetchVendors = async () => {
    setFetchingVendors(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/vendors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setVendors(response.data.vendors || []);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError('Failed to load vendors');
    } finally {
      setFetchingVendors(false);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (purchase) {
        // Edit mode - populate with existing data
        setVendor(purchase.vendor?._id || purchase.vendor || '');
        setVendorName(purchase.vendorName || '');
        setGst(purchase.gst || '');
        setInvoiceNumber(purchase.invoiceNumber || '');
        setDate(purchase.date ? new Date(purchase.date).toISOString().split('T')[0] : '');
        setSubTotal(purchase.subTotal?.toString() || '');
        setTax(purchase.tax?.toString() || '');
        setTotal(purchase.total?.toString() || '');
        setPurchaseStatus(purchase.status || 'Draft');
      } else {
        // Create mode - reset form
        setVendor('');
        setVendorName('');
        setGst('');
        setInvoiceNumber('');
        setDate('');
        setSubTotal('');
        setTax('');
        setTotal('');
        setPurchaseStatus('Draft');
      }
      setError('');
      setPayments([]);
    }
  }, [isOpen, purchase]);

  // Handle vendor selection
  const handleVendorChange = (e) => {
    const selectedVendorId = e.target.value;
    setVendor(selectedVendorId);
    
    const selectedVendor = vendors.find(v => v._id === selectedVendorId);
    if (selectedVendor) {
      setVendorName(selectedVendor.name);
      setGst(selectedVendor.gst || '');
    } else {
      setVendorName('');
      setGst('');
    }
  };

  // Auto-calculate total when subtotal or tax changes
  useEffect(() => {
    const subTotalValue = parseFloat(subTotal) || 0;
    const taxValue = parseFloat(tax) || 0;
    const calculatedTotal = subTotalValue + taxValue;
    setTotal(calculatedTotal.toFixed(2));
  }, [subTotal, tax]);

  const handleSavePurchase = async () => {
    // Validate fields
    const subTotalValue = parseFloat(subTotal);
    const taxValue = parseFloat(tax);
    const totalValue = parseFloat(total);
    
    if (!vendor) {
      setError('Please select a vendor');
      return;
    }

    if (!date) {
      setError('Date is required');
      return;
    }

    if (!subTotal || isNaN(subTotalValue) || subTotalValue < 0) {
      setError('Please enter a valid subtotal');
      return;
    }

    if (!tax || isNaN(taxValue) || taxValue < 0) {
      setError('Please enter a valid tax amount');
      return;
    }

    if (!total || isNaN(totalValue) || totalValue <= 0) {
      setError('Total must be greater than 0');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        vendor,
        vendorName,
        gst,
        invoiceNumber,
        date,
        subTotal: subTotalValue,
        tax: taxValue,
        total: totalValue
      };

      const response = isEditMode && isEditable
        ? await axios.put(
            `${API_BASE_URL}/purchases/${purchase._id}`,
            payload,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          )
        : await axios.post(
            `${API_BASE_URL}/purchases`,
            payload,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

      const number = response.data?.purchaseNumber || response.data?.invoiceNumber || purchase?.purchaseNumber || purchase?.invoiceNumber || 'purchase';
      const mode = isEditMode ? 'updated' : 'created';
      showInlineNotice(`Purchase ${number} ${mode} successfully.`, {
        scope: 'purchase-modal'
      });
      
      // Call the callback to update parent component
      if (onPurchaseSaved) {
        onPurchaseSaved(response.data);
      }

      // Close modal after 1 second
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (err) {
      console.error('Error saving purchase:', err);
      const errorMsg = err.response?.data?.error || 'Failed to save purchase';
      setError(errorMsg);
      showInlineNotice(errorMsg, {
        scope: 'purchase-modal',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const formatDate = (value) => {
    return value ? new Date(value).toLocaleDateString() : 'N/A';
  };

  const totalValue = parseFloat(total || purchase?.total || 0) || 0;
  const paidTotal = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const remainingBalance = Math.max(0, totalValue - paidTotal);
  const canAddPayment = ['Confirmed', 'In Progress'].includes(purchaseStatus) && purchaseStatus !== 'Cancelled';
  const formatCurrency = (value) => (
    formatCurrencyBySystem(Number(value || 0), getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    })
  );
  const currencyPrefix = getCurrencyPrefixFromStorage();

  const handlePaymentSaved = async () => {
    await fetchPaymentHistory();
    if (onPurchaseSaved) {
      onPurchaseSaved();
    }
    setPaymentModalOpen(false);
  };

  const handleConfirmPurchase = async () => {
    if (!purchase?._id || purchaseStatus !== 'Draft') return;
    try {
      setConfirming(true);
      await axios.patch(
        `${API_BASE_URL}/purchases/${purchase._id}`,
        { status: 'Confirmed' },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setPurchaseStatus('Confirmed');
      const number = purchase?.purchaseNumber || purchase?.invoiceNumber || 'purchase';
      showInlineNotice(`Purchase ${number} confirmed successfully.`, {
        scope: 'purchase-modal'
      });
      if (onPurchaseSaved) {
        onPurchaseSaved();
      }
    } catch (err) {
      console.error('Error confirming purchase:', err);
      setError('Failed to confirm purchase');
      showInlineNotice('Failed to confirm purchase.', {
        scope: 'purchase-modal',
        type: 'error'
      });
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'View Purchase' : 'Add Purchase'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-body" style={{ padding: '24px' }}>
          <InlineNotice scope="purchase-modal" />
          {error && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {isEditMode && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#374151'
            }}>
              <div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Vendor</div>
                <div style={{ fontWeight: '600' }}>{purchase?.vendorName || purchase?.vendor?.name || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Purchase No</div>
                <div style={{ fontWeight: '600' }}>{purchase?.purchaseNumber || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Vendor Invoice No</div>
                <div style={{ fontWeight: '600' }}>{purchase?.invoiceNumber || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Date</div>
                <div style={{ fontWeight: '600' }}>{formatDate(purchase?.date)}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Status</div>
                <div style={{ fontWeight: '600' }}>{purchaseStatus}</div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              Vendor *
            </label>
            <select
              value={vendor}
              onChange={handleVendorChange}
              disabled={loading || fetchingVendors || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: `1px solid ${error && !vendor ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || fetchingVendors || isReadOnly ? '#f3f4f6' : '#fff'
              }}
            >
              <option value="">Select vendor</option>
              {vendors.map((v) => (
                <option key={v._id} value={v._id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              GST
            </label>
            <input
              type="text"
              value={gst}
              onChange={(e) => setGst(e.target.value)}
              placeholder="GST Number (optional)"
              disabled={loading || fetchingVendors || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || fetchingVendors || isReadOnly ? '#f3f4f6' : '#fff'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              Vendor Invoice Number
            </label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Vendor invoice/bill number (optional)"
              disabled={loading || fetchingVendors || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || fetchingVendors || isReadOnly ? '#f3f4f6' : '#fff'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              Date *
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={loading || fetchingVendors || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: `1px solid ${error && !date ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || fetchingVendors || isReadOnly ? '#f3f4f6' : '#fff'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              Sub Total ({currencyPrefix}) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={subTotal}
              onChange={(e) => setSubTotal(e.target.value)}
              placeholder="Enter sub total"
              disabled={loading || fetchingVendors || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: `1px solid ${error && !subTotal ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || fetchingVendors || isReadOnly ? '#f3f4f6' : '#fff'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              Tax ({currencyPrefix}) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={tax}
              onChange={(e) => setTax(e.target.value)}
              placeholder="Enter tax amount"
              disabled={loading || fetchingVendors || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: `1px solid ${error && !tax ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || fetchingVendors || isReadOnly ? '#f3f4f6' : '#fff'
              }}
            />
          </div>

          <div style={{ marginBottom: isEditMode ? '20px' : '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              Total ({currencyPrefix}) *
            </label>
            <input
              type="number"
              step="0.01"
              value={total}
              disabled
              placeholder="Auto-calculated"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: '#f3f4f6',
                fontWeight: '600',
                color: '#059669'
              }}
            />
          </div>

          {/* Payment History - Only show in edit/view mode */}
          {isEditMode && (
            <div style={{
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '2px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  margin: 0,
                  color: '#1f2937'
                }}>
                  Payment History ({payments.length})
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {purchaseStatus === 'Draft' && (
                    <button
                      onClick={handleConfirmPurchase}
                      disabled={confirming}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: confirming ? 'not-allowed' : 'pointer',
                        opacity: confirming ? 0.6 : 1
                      }}
                    >
                      {confirming ? 'Confirming...' : 'Confirm'}
                    </button>
                  )}
                  <button
                    onClick={() => setPaymentModalOpen(true)}
                    disabled={fetchingPayments || !canAddPayment || remainingBalance <= 0}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: (fetchingPayments || !canAddPayment || remainingBalance <= 0) ? 'not-allowed' : 'pointer',
                      opacity: (fetchingPayments || !canAddPayment || remainingBalance <= 0) ? 0.6 : 1
                    }}
                  >
                    {remainingBalance <= 0 ? 'Completed' : 'Add Payment'}
                  </button>
                </div>
              </div>
              {fetchingPayments ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                    <path d="M3 21v-5h5"/>
                  </svg>
                  <p style={{ marginTop: '8px', color: '#6b7280' }}>Loading payments...</p>
                </div>
              ) : payments.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  color: '#6b7280'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px', display: 'block', opacity: 0.5 }}>
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                  <p style={{ margin: 0 }}>No payments recorded yet</p>
                </div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f3f4f6' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Date</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Amount</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Mode</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Reference</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Added By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment) => (
                          <tr key={payment._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '10px' }}>
                              {new Date(payment.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right', color: '#059669', fontWeight: '600' }}>
                              {formatCurrency(payment.amount)}
                            </td>
                            <td style={{ padding: '10px' }}>
                              <span style={{
                                padding: '4px 8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {payment.paymentMode}
                              </span>
                            </td>
                            <td style={{ padding: '10px', color: '#6b7280' }}>{payment.paymentReference}</td>
                            <td style={{ padding: '10px', color: '#6b7280' }}>{payment.createdBy?.name || 'Unknown'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot style={{ backgroundColor: '#f9fafb' }}>
                        <tr>
                          <td style={{ padding: '10px', fontWeight: '600', borderTop: '2px solid #e5e7eb' }}>Total Paid</td>
                          <td colSpan="4" style={{ padding: '10px', textAlign: 'right', color: '#059669', fontWeight: '700', borderTop: '2px solid #e5e7eb' }}>
                            {formatCurrency(payments.reduce((sum, p) => sum + p.amount, 0))}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '10px', fontWeight: '600' }}>Remaining</td>
                          <td colSpan="4" style={{ padding: '10px', textAlign: 'right', color: '#dc2626', fontWeight: '700' }}>
                            {formatCurrency(remainingBalance)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

        {canViewHistory && (
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            backgroundColor: '#fff'
          }}>
            <button
              type="button"
              onClick={() => setHistoryExpanded(prev => !prev)}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: '#1f2937'
              }}
            >
              <span>History</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                {historyExpanded ? 'Hide' : 'Show'}
              </span>
            </button>

            {historyExpanded && (
              <div style={{ marginTop: '12px' }}>
                {buildHistoryEntries().length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>No history yet.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ padding: '8px 6px', color: '#374151' }}>Action</th>
                          <th style={{ padding: '8px 6px', color: '#374151' }}>DateTime</th>
                          <th style={{ padding: '8px 6px', color: '#374151' }}>Author</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildHistoryEntries().map((entry, index) => (
                          <tr key={`${entry.action}-${index}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 6px', color: '#1f2937' }}>{entry.action}</td>
                            <td style={{ padding: '8px 6px', color: '#374151' }}>{formatDateTime(entry.dateTime)}</td>
                            <td style={{ padding: '8px 6px', color: '#374151' }}>{entry.author}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        </div>

        <div className="modal-footer" style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#1f2937',
              opacity: loading ? 0.6 : 1
            }}
          >
            {isEditMode ? 'Close' : 'Cancel'}
          </button>
          {(!isEditMode || isEditable) && (
            <button
              onClick={handleSavePurchase}
              disabled={loading || fetchingVendors || !vendor || !date || !subTotal || !tax || !total}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: (loading || fetchingVendors || !vendor || !date || !subTotal || !tax || !total) ? 'not-allowed' : 'pointer',
                opacity: (loading || fetchingVendors || !vendor || !date || !subTotal || !tax || !total) ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {loading && (
                <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                  <path d="M3 21v-5h5"/>
                </svg>
              )}
              {loading ? 'Saving...' : (isEditMode && isEditable ? 'Update' : 'Submit')}
            </button>
          )}
        </div>
        </div>
      </div>

      {isEditMode && purchase?._id && (
        <UnifiedPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          paymentType="purchase"
          entity={purchase}
          onPaymentSaved={handlePaymentSaved}
        />
      )}
    </>
  );
};

export default PurchaseModal;
